import evaluateExperiments from "./evaluation/experiment";
import store from "./evaluation/store";
import { getMockInfo, applyAbbaPreviewConfig } from "./evaluation/preview";
import ConfigManager from "./evaluation/configManager";
import { ByzantiumCore } from "./Byzantium/lib/byzantiumCore";
import BridgeWeb from "./Bridge/Web/bridgeWeb";
import debugTools from "./evaluation/debug";
import { URL_KEYS } from "./constants/urlKeys";
import argumentVerifier from "./util/argumentVerifier";
import packageJson from "./package.json";
import { jobQueue } from "./evaluation/jobQueue";
import sanitizeFeed from "./util/sanitizeFeed";
import evaluateCondition from "./evaluation/condition";
import { evaluateUrlInfo } from "./evaluation/rule";
import getImpersonatorData from "./util/getImpersonatorData";
import merge from "lodash.merge";

const USER_INFO_TIMEOUT = 2000;
const USER_INFO_EVENT = "reg-user-info-ready";

// If window.performance isn't available [not on web], use a no-op placeholder instead
const performance = window?.performance?.mark
    ? window.performance
    : {
        mark: () => ({}),
        measure: () => ({}),
    };

export default class AbbaClient {
    /**
     * The AbbaClient class constructor
     * @param {string} platform - The platform abba-client is present in [if not defined, defaults to "frontend"]
     * @param {Bridge} bridge - Optional bridge override if default bridge-web isn't desired [bridge-mobile for example]
     * @param {Object} options - Optional flags to change abbaClient behavior. See options available below.
      * useCachedCoordinatorData {Boolean} - Allows returning stale coordinatorServiceData while queuing a background fetch call
      * forceDebugLogLevel {"error" | "info" | "debug"} - Force debugging to be enabled at the given log level
      *                                                   rather than from a query param
      * suppressDebugPopup {Boolean} - Do not show the debug popup window
     */
    constructor({
        platform = "frontend",
        bridge = null,
        options = { useCachedCoordinatorData: true, forceDebugLogLevel: null, suppressDebugPopup: false },
    } = {}) {
        this._bindClassFunctions(this);
        debugTools._init(options.forceDebugLogLevel, options.suppressDebugPopup);
        debugTools.info(`Abba-Client version ${ packageJson.version }`);

        this._platform = platform;

        this._assignedTargetIds = new Set();
        this._assignedExperimentIds = new Set();

        this._bridge = bridge ?? new BridgeWeb();
        store.setBridge(this._bridge);
        debugTools.debug("Bridge set");

        this._byzantium = new ByzantiumCore(this._bridge, {
            useCachedCoordinatorData: options.useCachedCoordinatorData ?? true,
        });
        debugTools.debug("Byzantium initialized");
    }

    _bindClassFunctions(self) {
        self._init = self._init.bind(self);
        self.getConfig = self.getConfig.bind(self);
        self._getConfig = self._getConfig.bind(self);
        self.getAccesslistMembership = self.getAccesslistMembership.bind(self);
        self.getAdParams = self.getAdParams.bind(self);
        self.getAdRecommendations = self.getAdRecommendations.bind(self);
        self.getDeviceInfo = self.getDeviceInfo.bind(self);
        self.getGeoIPData = self.getGeoIPData.bind(self);
        self.getPreferredAuthors = self.getPreferredAuthors.bind(self);
        self.getReferrerSource = self.getReferrerSource.bind(self);
        self.getSTRValues = self.getSTRValues.bind(self);
        self.getTags = self.getTags.bind(self);
        self.getPreferredTags = self.getPreferredTags.bind(self);
        self.getUserCredentials = self.getUserCredentials.bind(self);
        self.getUserInfo = self.getUserInfo.bind(self);
        self.setPersonalizationAllowedForMobileApp = self.setPersonalizationAllowedForMobileApp.bind(self);
        self._getRegSubUserInfo = self._getRegSubUserInfo.bind(self);
        self.getExperiencePreferenceFromCookie = self.getExperiencePreferenceFromCookie.bind(self);
    }

    /**
     * Initializes an AbbaClient instance for providing A/B testing configurations when provided page information + abba feeds
     * @param {string} platform - The platform abba-client is present in [if not defined, defaults to "frontend"]
     * @param {Bridge} bridge - Optional bridge override if default bridge-web isn't desired [bridge-mobile for example]
     * @param {Object} contentMetadata - Article and page metadata required for byzantium to make calls with proper page context
     * @param {Object} abbaFeed - Optional abbaFeed override will prevent abbaFeed fetch calls and use the provided object instead
     * @param {Object} coordinatorQueryParams - Optional key/value pairs of queryParams to send over coordinatorServiceDataCalls
     * @param {Object} options - Optional flags to change abbaClient behavior. See options available below.
        * useCachedCoordinatorData {Boolean} - Allows returning stale coordinatorServiceData while queuing a background fetch call
        * forceCoordinatorFetch {Boolean} - Forces the init setup call to fetch fresh coordinatorServiceData unconditionally
        * forceDebugLogLevel {string} - Force debugging to be enabled at the given log level, rather than from a query param
        * suppressDebugPopup {Boolean} - Do not show the debug popup window
     */
    static async build({
        platform = "frontend",
        bridge = null,
        contentMetadata = {},
        abbaFeeds = null,
        coordinatorQueryParams = {},
        options = {
            useCachedCoordinatorData: true,
            forceCoordinatorFetch: true,
            forceDebugLogLevel: null,
            suppressDebugPopup: false,
        },
    } = {}) {
        const abbaClient = new AbbaClient({ platform, bridge, options });

        await abbaClient._init({
            contentMetadata,
            abbaFeeds,
            coordinatorQueryParams,
            options,
        });

        return abbaClient;
    }

    /**
     * Queues [doesn't await!] the mandatory Abba-Client setup calls, fetching and setting required data for getConfig calls
     * @param {Object} contentMetadata - Article and page metadata required for byzantium to make calls with proper page context
     * @param {Object} abbaFeed - Optional abbaFeed override will prevent abbaFeed fetch calls and use the provided object instead
     * @param {Object} coordinatorQueryParams - Optional key/value pairs of queryParams to send over coordinatorServiceDataCalls
     * @param {Object} options - Optional flags to change setup behavior. See options available below.
        * forceCoordinatorFetch {Boolean} - Forces the init setup call to fetch fresh coordinatorServiceData unconditionally
    */
    async _init({
        contentMetadata = {},
        abbaFeeds = null,
        coordinatorQueryParams = {},
        options = { forceCoordinatorFetch: false },
    } = {}) {
        performance.mark("AbbaClient-_init-Start");

        const impersonatorMode = await this._bridge.getItem("inImpersonatorMode", true) === true;
        const impersonatorExtensionId = await this._bridge.getItem("impersonatorExtensionId", true);

        if (impersonatorMode && impersonatorExtensionId) {
            this._impersonatorMockData = await getImpersonatorData(impersonatorExtensionId);
        } else if (impersonatorMode && !impersonatorExtensionId) {
            debugTools.error(`In impersonator mode but missing extension id. Value was: ${ impersonatorExtensionId }`);
        }

        debugTools.debug("Queueing Byzantium setContentMetadata and refreshCoordinatorServiceData calls");

        // Queue setup actions in the jobQueue, but *don't* await them. This means setup calls will resolve in the background
        // When getConfig is called, it awaits the result of its pushed job, which waits on prior queued calls to resolve first
        jobQueue.push(async () => {
            const coordinatorCalls = async () => {
                await this._byzantium.setContentMetadata(contentMetadata);

                return await this._byzantium.refreshCoordinatorServiceData({
                    forceCoordinatorFetch: options.forceCoordinatorFetch ?? false,
                    coordinatorQueryParams,
                });
            };
            const abbaFeedsCall = async () => {
                if (abbaFeeds) {
                    return { data: abbaFeeds, wasBlockingCall: false };
                }

                return this._byzantium.refreshFeeds(this._platform);
            };

            const abbaInfoPromises = [
                coordinatorCalls(),
                abbaFeedsCall(),
            ];

            const [coordinatorServiceData, feedsData] = await Promise.all(abbaInfoPromises);

            this._feeds = feedsData.data;

            performance.mark("AbbaClient-_init-End");
            const measure = performance.measure("AbbaClient-_init", "AbbaClient-_init-Start", "AbbaClient-_init-End");

            window?.newrelic?.addPageAction("AbbaClient-_init", {
                duration: measure.duration,
                neededBlockingCalls: coordinatorServiceData.wasBlockingCall || feedsData.wasBlockingCall,
                blockedByCoordinator: coordinatorServiceData.wasBlockingCall,
                blockedByFeeds: feedsData.wasBlockingCall,
            });

            debugTools.debug("Resolved Byzantium getAbbaFeeds, setContentMetadata and refreshCoordinatorServiceData calls");
        });
    }

    /**
     * Refresh is an alias function for _init. The name _init doesn't lend itself for function
     * calls beyond the initial call for setup, so this gets around that confusion.
     */
    refresh = this._init

    /**
     * Get the config to use from the set of targeting/testing rules from ABBA, given user information.
     * @param {Object} platformObjectKey - The platform object feed name the ABBA config is desired for.
     * @param {Object} [userInfo={}] - Optional extra user information to use for targeting
     */
    async getConfig(platformObjectKey, userInfo = {}) {
        if (!platformObjectKey) {
            throw new Error("You must provide a platformObject name for getConfig to obtain its abba target/experiment feed.");
        }

        debugTools.info(`Queueing getConfig call for platformObject: ${ platformObjectKey }`);

        // Awaiting jobQueue.push waits for all previously queued jobs to resolve first (_init calls, other getConfig calls)
        const config = await jobQueue.push(async () => {
            const feed = this?._feeds[platformObjectKey] ?? null;

            if (!feed) {
                throw new Error(
                    `The feed for platform object ${ platformObjectKey } is not present in the ${ this._platform } feeds object`
                );
            }

            const evaluatedConfig = await this._getConfig(feed, userInfo);

            debugTools.info(`Resolved getConfig call for platformObject: ${ platformObjectKey }`);

            return evaluatedConfig;
        });

        return config;
    }

    /**
     * Get the config to use from the set of targeting/testing rules from ABBA, given user information.
     * @param {Object} feed - The platform object feed provided by coming from the ABBA dashboard on init.
     * @param {Object} [userInfo={}] - Optional extra user information to use for targeting
     */
    async _getConfig(feed, userInfo = {}) {
        try {
            feed = sanitizeFeed(feed);
            argumentVerifier.verifyGetConfigArguments(feed);

            debugTools.info("Received valid feed", feed);
            debugTools.info("Provided user info", userInfo);

            const targetingMetadata = await this._byzantium.getTargetingMetadata();
            const {
                contextInfo = getMockInfo(URL_KEYS.ABBA_MOCK_CONTEXTINFO_KEY),
                tagsInfo = getMockInfo(URL_KEYS.ABBA_MOCK_TAGSINFO_KEY),
                urlUtmValues = getMockInfo(URL_KEYS.ABBA_MOCK_URLINFO_KEY),
                contentMetadata = getMockInfo(URL_KEYS.ABBA_MOCK_CONTENT_METADATA),
                byzantium,
                geoIP,
            } = targetingMetadata;

            if (getMockInfo(URL_KEYS.ABBA_MOCK_USERINFO_KEY)) {
                // legacy mock approach. eventually remove this before impersonator v1.0 release
                userInfo = getMockInfo(URL_KEYS.ABBA_MOCK_USERINFO_KEY);
            } else {
                userInfo = Object.assign(
                    {},
                    userInfo,
                    { byzantium },
                    { contextInfo },
                    { tagsInfo },
                    { urlUtmValues },
                    { contentMetadata },
                    { geoIP }
                );

                if (this._impersonatorMockData) {
                    userInfo = merge(userInfo, this._impersonatorMockData ?? {});
                }
            }

            debugTools.info("Final user info set", userInfo);

            const cachedConditions = {};
            const cm = new ConfigManager();

            debugTools.debug("Evaluating experiments...");

            for (let i = feed.targets.length - 1; i >= 0; i--) {
                const target = feed.targets[i];
                const userIsLockedInBucket = await store.lockedInExperiment(target);
                const ruleEvaluationResult = await evaluateCondition(target.condition, userInfo);
                const meetsCondition = (userIsLockedInBucket) || (ruleEvaluationResult);

                if (meetsCondition) {
                    debugTools.info(`Experiment evaluation met the condition for target ${ target.targetId }
                                     the user is locked into bucked value is ${ userIsLockedInBucket }
                                     the target's rule evaluation result is ${ ruleEvaluationResult })`, target);
                    let selectedExperiment;

                    try {
                        selectedExperiment = await evaluateExperiments(target);
                    } catch (e) {
                        debugTools.error(
                            `Attempted to evaluate experiments for the target ${ target.targetId } but an error occurred.`,
                            {
                                target,
                                error: e,
                            }
                        );
                        continue;
                    }

                    if (selectedExperiment) {
                        const trackingString
                            = `${ target.targetId }.${ selectedExperiment.experimentId }=${ selectedExperiment.bucketId }`;
                        let configAdded, experimentPlacement;

                        try {
                            experimentPlacement = await store.getExperimentPlacement(selectedExperiment.experimentId);
                            configAdded = cm.addExperimentConfig(
                                selectedExperiment.config,
                                trackingString,
                                selectedExperiment.endTime,
                                experimentPlacement
                            );
                        } catch (e) {
                            debugTools.error(
                                `Attempted to get the previous placement for experiment ${ selectedExperiment.experimentId }
                                 and add it to the config object but an error occurred.`,
                                {
                                    experimentPlacement,
                                    error: e,
                                }
                            );
                            continue;
                        }

                        if (configAdded) {
                            // evaluateExperiments adds the experiment and target placement to the store
                            debugTools.info(
                                `Placed into experiment ${ selectedExperiment.experimentId }
                                 in bucket ${ selectedExperiment.bucketId }
                                 of target ${ target.targetId }`,
                                selectedExperiment
                            );
                        } else {
                            debugTools.info(
                                `Attempted to place into experiment ${ selectedExperiment.experimentId }
                                 in bucket ${ selectedExperiment.bucketId }
                                 of target ${ target.targetId }, but there were overlapping configurations.`,
                                selectedExperiment
                            );
                        }
                    } else {
                        debugTools.info(`User did not fall into any experiment in ${ target.targetId }`);
                    }
                } else {
                    debugTools.info(`Experiment evaluation did not meet the condition for target ${ target.targetId }
                                     the user is locked into bucked value is (${ userIsLockedInBucket })
                                     the target's rule evaluation is ${ ruleEvaluationResult }`, target);
                }
            }

            debugTools.debug("Evaluating targets...");

            for (let i = feed.targets.length - 1; i >= 0; i--) {
                const target = feed.targets[i];
                const meetsCondition = await evaluateCondition(target.condition, userInfo);

                if (meetsCondition) {
                    let configAdded, targetPlacement;

                    try {
                        targetPlacement = await store.getTargetPlacement(target.targetId);
                        configAdded = cm.addTargetConfig(
                            target.config,
                            target.targetId,
                            targetPlacement
                        );
                    } catch (e) {
                        debugTools.error(
                            `Attempted to get the previous placement for target
                             ${ target.targetId } and add it to the config object but an error occurred.`,
                            {
                                targetPlacement,
                                error: e,
                            }
                        );
                        continue;
                    }

                    if (configAdded) {
                        await store.setTargetPlacement(target.targetId, null, null);
                        debugTools.info(
                            `Target ${ target.targetId } configuration applied`,
                            target
                        );
                    } else {
                        debugTools.info(
                            `Attempted to apply target ${ target.targetId } but there were overlapping configurations.`,
                            target
                        );
                    }
                }
            }

            debugTools.debug("Updating Tracking History");
            cm.tracking.targets.forEach((target) => this._assignedTargetIds.add(target));
            cm.tracking.experiments.forEach((experiment) => this._assignedExperimentIds.add(experiment));

            debugTools.debug("Applying preview configs");
            const config = await applyAbbaPreviewConfig(cm.getConfig());

            debugTools.info("Final configuration", config);

            return config;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            debugTools.error("Error in processing", err);

            throw err;
        }
    }

    /**
     * Expose the evaluateUrlInfo rule function used for abba decisioning.
     */
    evaluateUrlInfo = evaluateUrlInfo

    async _getRegSubUserInfo() {
        // The Mobile App doesn't have a window object
        if (window === undefined) {
            return undefined;
        }

        if (window?._regUserInfo){
            return window?._regUserInfo;
        } else {
            return new Promise((resolve, reject) => {
                const regInfoEventListener = (_event) => {
                    window.removeEventListener(USER_INFO_EVENT, regInfoEventListener);
                    resolve(window._regUserInfo);
                };

                window.addEventListener(USER_INFO_EVENT, regInfoEventListener);

                setTimeout(() => {
                    window.removeEventListener(USER_INFO_EVENT, regInfoEventListener);
                    resolve(undefined);
                }, USER_INFO_TIMEOUT);
            });
        }
    }

    getPageTracking() {
        return {
            targets: Array.from(this._assignedTargetIds),
            experiments: Array.from(this._assignedExperimentIds),
        };
    }

    getExperiencePreferenceFromCookie() {
        let exp_pref;

        try {
            exp_pref = this._bridge.getCookie("exp_pref");
        } catch (error) {
            debugTools.error("Error in getting exp_pref cookie", error);
        }

        return exp_pref || "AMER";
    }

    async getFortressData() {
        // This function purely collates calls which use the jobQueue. If we queue also queue this function, it'll never resolve
        const promises = [
            this.getUserCredentials(),
            this.getGeoIPData(),
            this.getUserInfo(),
            this.getAccesslistMembership(),
            this.getExtendedAccessUserState(),
            this.getPersonalizationAllowed(),
        ];

        const [
            userCredentials,
            geoIPData,
            userInfo,
            accesslistMembership,
            extendedAccessUserState,
            personalizationAllowed,
        ] = await Promise.all(promises);

        return {
            userCredentials,
            geoIPData,
            userInfo,
            accesslistMembership,
            extendedAccessUserState,
            personalizationAllowed,
        };
    }

    /**
     * Byzantium and Bridge Function Calls
     * These are "private" libraries within abba-Client, so we only want to expose only some of their functionality
     * Queueing these calls jobQueue ensures abbaClient build, which fetches the data, will resolve first
     */
    async getAccesslistMembership() {
        return await jobQueue.push(async () => {
            return this._byzantium.getAccesslistMembership();
        });
    }

    async getAdParams(params) {
        return await jobQueue.push(async () => {
            return this._byzantium.getAdParams(params);
        });
    }

    async getAdRecommendations(adTargeting, filters) {
        return await jobQueue.push(async () => {
            return await this._byzantium.getAdRecommendations(adTargeting, filters);
        });
    }

    async getAdRecommendationsPotentiallyStale(adTargeting, filters) {
        return this._byzantium.getAdRecommendations(adTargeting, filters);
    }

    async getContextMetadata() {
        return this._byzantium.getContextMetadata();
    }

    getDeviceInfo() {
        return this._byzantium.getDeviceInfo();
    }

    async getExtendedAccessUserState() {
        return await jobQueue.push(async () => {
            return await this._byzantium.getExtendedAccessUserState();
        });
    }

    async getGeoIPData() {
        return await jobQueue.push(async () => {
            return await this._byzantium.getGeoIPData();
        });
    }

    async getPersonalizationAllowed() {
        return await jobQueue.push(async () => {
            return this._bridge.getPersonalizationAllowed();
        });
    }

    async getPreferredAuthors() {
        return await jobQueue.push(async () => {
            return this._byzantium.getPreferredAuthors();
        });
    }

    async getReferrerSource() {
        return await this._byzantium.getReferrerSource();
    }

    async getSTRValues() {
        return await jobQueue.push(async () => {
            return await this._byzantium.getSTRValues();
        });
    }

    async getTags(adTargeting, contentInfo) {
        return await jobQueue.push(async () => {
            return await this._byzantium.getTags(adTargeting, contentInfo);
        });
    }

    async getTagsPotentiallyStale(adTargeting, contentInfo) {
        return this._byzantium.getTags(adTargeting, contentInfo);
    }

    async getPreferredTags(params) {
        return await jobQueue.push(async () => {
            return this._byzantium.getPreferredTags(params);
        });
    }

    async getUserCredentials() {
        return await jobQueue.push(async () => {
            return await this._byzantium.getUserCredentials();
        });
    }

    async getUserInfo() {
        return await jobQueue.push(async () => {
            return await this._byzantium.getUserInfo();
        });
    }

    async getUserInfoPotentiallyStale() {
        return this._byzantium.getUserInfo();
    }

    // This function will effectively no-op if on Bridge-Web
    async setPersonalizationAllowedForMobileApp(personalizationAllowed) {
        return await jobQueue.push(async () => {
            return await this._bridge.setPersonalizationAllowed(personalizationAllowed);
        });
    }
}

window?.document?.dispatchEvent(new CustomEvent("abbaClient:loaded"));
